// src/components/PlaybookSelectCard/PlaybookSelectCard.tsx
// =================================================================
// PLAYBOOK SELECT CARD
// =================================================================
//
// A single-select in-chat card shown when the backend returns a playbook
// selection request (Catylex multi-playbook tenants). The user picks one
// playbook; the choice is persisted for the session and the same chat turn
// continues with the chosen playbook.
//
// Self-contained styles — no shared icon/style imports.
// =================================================================

import * as React from "react";
import { useState } from "react";
import { PlaybookSelectionOption, PlaybookSelectionResult } from "../../interfaces/playbookTypes";

const ACCENT = "#0A8CBB";

interface PlaybookSelectCardProps {
  /** The normalized playbook selection request */
  result: PlaybookSelectionResult;
  /** Whether the user has already picked an option (locks the card) */
  settled: boolean;
  /** Name of the chosen playbook (for the settled acknowledgment) */
  selectedName?: string;
  /** Disable interaction (e.g. while a response is generating) */
  disabled?: boolean;
  /** Called with the chosen option when the user confirms */
  onSelect: (option: PlaybookSelectionOption) => void;
}

/**
 * Format an ISO date string as a short local date, guarding against invalid
 * dates (returns empty string so the meta line can omit it).
 */
function formatShortDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

const BookIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ACCENT}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const CheckIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 14,
  color = "#FFFFFF",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlaybookSelectCard: React.FC<PlaybookSelectCardProps> = ({
  result,
  settled,
  selectedName,
  disabled = false,
  onSelect,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // =================================================================
  // STYLES
  // =================================================================

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #E0E0E0",
    borderRadius: 12,
    overflow: "hidden",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: "#212529",
    width: "100%",
    marginBottom: 12,
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "11px 14px",
    borderBottom: "1px solid #F0F0F0",
    background: "#FFFFFF",
  };

  const headerLabelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 590,
    color: "#212529",
  };

  const optionsContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "12px 14px",
  };

  const footerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 14px 14px",
  };

  // =================================================================
  // SETTLED / LOCKED VIEW
  // =================================================================

  if (settled) {
    return (
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={headerLabelStyle}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: 5,
                background: ACCENT,
                flexShrink: 0,
              }}
            >
              <CheckIcon size={12} />
            </span>
            Using {selectedName || "the selected playbook"}. Proceeding…
          </span>
        </div>
      </div>
    );
  }

  // =================================================================
  // INTERACTIVE VIEW
  // =================================================================

  const handleConfirm = () => {
    if (disabled || !selectedId) return;
    const chosen = result.playbooks.find((p) => p.id === selectedId);
    if (chosen) {
      onSelect(chosen);
    }
  };

  const renderOption = (option: PlaybookSelectionOption, index: number) => {
    const isSelected = selectedId === option.id;
    const isHovered = hoveredId === option.id;

    const rowStyle: React.CSSProperties = {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "8px 10px",
      border: `1px solid ${isSelected ? ACCENT : "#EDEDED"}`,
      borderRadius: 8,
      background: isSelected ? `${ACCENT}10` : isHovered ? "#F5F7F8" : "#FFFFFF",
      cursor: disabled ? "default" : "pointer",
      textAlign: "left",
      width: "100%",
      transition: "background 0.12s ease, border-color 0.12s ease",
    };

    const boxStyle: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: 5,
      flexShrink: 0,
      background: isSelected ? ACCENT : "#F0F2F3",
      color: isSelected ? "#FFFFFF" : "#757575",
      fontSize: 12,
      fontWeight: 600,
    };

    const nameStyle: React.CSSProperties = {
      fontSize: 13,
      fontWeight: isSelected ? 590 : 400,
      color: "#212529",
      lineHeight: 1.4,
    };

    const descStyle: React.CSSProperties = {
      fontSize: 12,
      color: "#757575",
      marginTop: 2,
      lineHeight: 1.4,
    };

    const metaStyle: React.CSSProperties = {
      fontSize: 11,
      color: "#9E9E9E",
      marginTop: 4,
    };

    const metaParts: string[] = [];
    if (option.status) metaParts.push(`Status: ${option.status}`);
    const formattedDate = formatShortDate(option.last_updated);
    if (formattedDate) metaParts.push(`Updated ${formattedDate}`);
    if (option.id) metaParts.push(`ID ${option.id}`);

    return (
      <button
        key={option.id}
        type="button"
        style={rowStyle}
        disabled={disabled}
        onClick={() => !disabled && setSelectedId(option.id)}
        onMouseEnter={() => setHoveredId(option.id)}
        onMouseLeave={() => setHoveredId((prev) => (prev === option.id ? null : prev))}
        aria-pressed={isSelected}
      >
        <span style={boxStyle}>{isSelected ? <CheckIcon size={13} /> : index + 1}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={nameStyle}>{option.name}</span>
          {option.description && <div style={descStyle}>{option.description}</div>}
          {metaParts.length > 0 && <div style={metaStyle}>{metaParts.join(" · ")}</div>}
        </span>
      </button>
    );
  };

  const confirmDisabled = disabled || !selectedId;

  const buttonStyle: React.CSSProperties = {
    background: confirmDisabled ? "#B4DAE8" : ACCENT,
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 590,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    cursor: confirmDisabled ? "default" : "pointer",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={headerLabelStyle}>
          <BookIcon />
          {result.reason || "Choose a playbook"}
        </span>
      </div>

      <div style={optionsContainerStyle}>
        {result.playbooks.map((option, index) => renderOption(option, index))}
      </div>

      <div style={footerStyle}>
        <button
          type="button"
          style={buttonStyle}
          disabled={confirmDisabled}
          onClick={handleConfirm}
        >
          Use this playbook
        </button>
      </div>
    </div>
  );
};

export default PlaybookSelectCard;
